/**
 * Agent Custodian Client
 * Frontend client for interacting with Agent Custodian API
 */

import axios from 'axios';

export interface ConversationStartResult {
  success: boolean;
  conversation_id: string;
  stage: string;
  reply: string;
  questions?: string[];
  partial_agent?: any;
}

export interface ConversationMessageResult {
  success: boolean;
  conversation_id: string;
  stage: string;
  reply: string;
  questions?: string[];
  partial_agent?: any;
  agent_definition?: any;
}

export interface ConversationState {
  success: boolean;
  conversation: {
    id: string;
    stage: string;
    messages: Array<{
      role: 'user' | 'assistant';
      content: string;
      timestamp: Date;
    }>;
    partial_agent?: any;
    agent_definition?: any;
    created_at: Date;
    updated_at: Date;
  };
}

export interface TestRunResult {
  success: boolean;
  test_run_id: string;
  passed: boolean;
  metrics: {
    latency_ms: number;
    cost_usd: number;
    accuracy_score: number | null;
    tokens_used?: number;
    step_count: number;
  };
  output: any;
  step_metrics?: Array<{
    step_index: number;
    step_name: string;
    latency_ms: number;
    tokens_used: number;
    cost_usd: number;
    provider: string;
    model: string;
  }>;
  evaluation?: {
    score: number | null;
    commentary: string;
  };
  error?: string;
}

export interface TestHistory {
  success: boolean;
  agent_id: string;
  history: any[];
  stats: {
    total_tests: number;
    passed_tests: number;
    failed_tests: number;
    success_rate: number;
    avg_latency_ms: number;
    avg_cost_usd: number;
    avg_accuracy_score: number;
    last_test_run?: Date;
  };
}

export interface EcosystemGraph {
  success: boolean;
  nodes: Array<{
    id: string;
    label: string;
    type: 'internal' | 'app';
    domain: string;
    stage: string;
    status: string;
    description: string;
    metrics: {
      invocations: number;
      success_rate: number;
    };
  }>;
  edges: Array<{
    source: string;
    target: string;
    type: string;
    label: string;
    metadata?: any;
  }>;
  summary: {
    total_agents: number;
    internal_agents: number;
    app_agents: number;
    total_connections: number;
    by_domain: Record<string, number>;
    by_stage: Record<string, number>;
  };
}

export class CustodianClient {
  private baseUrl: string;

  constructor() {
    // Auto-detect base URL based on environment
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      this.baseUrl = 'http://localhost:8093'; // agent-creator service
    } else {
      this.baseUrl = window.location.origin + '/cp'; // production via Kong (routes to agent-creator service with /cp prefix)
    }
  }

  /**
   * Start a new agent creation conversation
   */
  async startConversation(message: string): Promise<ConversationStartResult> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/conversations/start`,
        {
          message,
          user_id: this.getUserId(),
          tenant_id: this.getTenantId()
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Continue an existing conversation
   */
  async sendMessage(conversationId: string, message: string): Promise<ConversationMessageResult> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/conversations/${conversationId}/message`,
        { message }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Get conversation state
   */
  async getConversation(conversationId: string): Promise<ConversationState> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/agent-custodian/conversations/${conversationId}`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Run agent in testing sandbox
   */
  async runSandboxTest(
    agentId: string,
    testInput: any,
    options?: {
      expected_output?: any;
      max_latency_ms?: number;
      max_cost_usd?: number;
      min_accuracy?: number;
      evaluate?: boolean;
    }
  ): Promise<TestRunResult> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/test-sandbox/run`,
        {
          agent_id: agentId,
          test_input: testInput,
          options
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Get test history for an agent
   */
  async getTestHistory(agentId: string, limit: number = 10): Promise<TestHistory> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/tests`,
        { params: { limit } }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Get ecosystem graph
   */
  async getEcosystemGraph(filters?: {
    agent_type?: 'internal' | 'app';
    domain?: string;
    deployment_stage?: 'draft' | 'staging' | 'production';
    status?: string;
  }): Promise<EcosystemGraph> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/agent-custodian/ecosystem/graph`,
        { params: filters }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Add connection between agents
   */
  async addConnection(
    sourceAgentId: string,
    targetAgentId: string,
    connectionType: 'depends_on' | 'feeds_into' | 'triggers' | 'uses_data_from',
    metadata?: any
  ): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/agents/${sourceAgentId}/connections`,
        {
          target_agent_id: targetAgentId,
          connection_type: connectionType,
          metadata
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Get agent connections
   */
  async getAgentConnections(agentId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/connections`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Analyze impact of changing an agent
   */
  async analyzeImpact(agentId: string): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/impact`
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Archive an agent
   */
  async archiveAgent(agentId: string, reason: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/archive`,
        {
          reason,
          archived_by: this.getUserId()
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Restore an archived agent
   */
  async restoreAgent(agentId: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/restore`,
        {
          restored_by: this.getUserId()
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Get archived agents
   */
  async getArchivedAgents(filters?: {
    agent_type?: 'internal' | 'app';
    archived_by?: string;
  }): Promise<any> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/api/agent-custodian/archived-agents`,
        { params: filters }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Edit an existing agent via conversation
   */
  async editAgent(agentId: string, message: string): Promise<ConversationStartResult> {
    try {
      const response = await axios.put(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/edit`,
        {
          message,
          user_id: this.getUserId(),
          tenant_id: this.getTenantId()
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Suggest changes to an agent workflow
   */
  async suggestAgentChanges(agentId: string, userRequest: string): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/suggest-changes`,
        {
          agent_id: agentId,
          user_request: userRequest
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Update an agent's workflow
   */
  async updateAgent(agentId: string, workflow: any): Promise<any> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/api/agent-custodian/agents/${agentId}/update-workflow`,
        {
          volcano_workflow: workflow
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || error.message);
    }
  }

  /**
   * Helper: Get user ID (from localStorage or session)
   */
  private getUserId(): string {
    // TODO: Implement actual user ID retrieval
    return localStorage.getItem('userId') || 'anonymous';
  }

  /**
   * Helper: Get tenant ID (from localStorage or session)
   */
  private getTenantId(): string {
    // TODO: Implement actual tenant ID retrieval
    return localStorage.getItem('tenantId') || 'default-tenant';
  }
}

// Singleton instance
export const custodianClient = new CustodianClient();

